import React from "react";
import ProductCard from "./ProductCard";
import "./CategorySection.css";

// Helper: determine if a product is sold out based on the soldOutItems map
const isProductSoldOut = (product, soldOutItems) => {
  if (!soldOutItems || Object.keys(soldOutItems).length === 0) {
    return false;
  }

  // Check if the product itself is sold out
  if (product.skuCode && product.skuCode in soldOutItems) {
    return true;
  }

  // Check if product has variants
  const variantGroup = product.variations?.find(v => v.isVariantGroup);
  if (!variantGroup || !variantGroup.options || variantGroup.options.length === 0) {
    return false;
  }

  // If there's only one variant and it's sold out
  if (variantGroup.options.length === 1) {
    const singleVariant = variantGroup.options[0];
    if (singleVariant.skuCode && singleVariant.skuCode in soldOutItems) {
      return true;
    }
  }

  // If there are multiple variants, check if ALL are sold out
  if (variantGroup.options.length > 1) {
    const allVariantsSoldOut = variantGroup.options.every(option =>
      option.skuCode && option.skuCode in soldOutItems
    );
    if (allVariantsSoldOut) {
      return true;
    }
  }

  return false;
};

export default function CategorySection({
  category,
  onProductClick,
  onAddToCart,
  onDecreaseFromCart,
  getProductCartQty,
  getProductCartLineCount,
  tags,
  subcategoryRefs,
  highlightText,
  searchTerm,
  soldOutItems,
  isViewOnly = false,
}) {
  // Group products by subCategoryName
  const productsBySubCategory = category.products.reduce((acc, product) => {
    const subCat = product.subCategoryName || ""; // empty string for products directly under parent
    if (!acc[subCat]) acc[subCat] = [];
    acc[subCat].push(product);
    return acc;
  }, {});

  return (
    <div className="category-section">
      {/* Parent Category Name */}
      <h2 className="category-title">{category.parentName}</h2>

      {/* Category Description */}
      {category.description && (
        <p className="category-description">{category.description}</p>
      )}

      {/* Products grouped by subcategory */}
      {Object.entries(productsBySubCategory).map(([subCatName, products]) => {
        // Create a unique key for this subcategory
        const subcategoryKey = subCatName ? `${category.id}-${subCatName}` : `${category.id}-default`;

        return (
          <div
            key={subCatName}
            ref={(el) => {
              if (subcategoryRefs && subCatName) {
                subcategoryRefs.current[subcategoryKey] = el;
              }
            }}
          >
            {subCatName && <h3 className="subcategory-title">{subCatName}</h3>}

            <div className="products-grid">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  tags={tags}
                  onClick={onProductClick}
                  onAddToCart={onAddToCart}
                  onDecreaseFromCart={onDecreaseFromCart}
                  cartLineCount={getProductCartLineCount ? getProductCartLineCount(product) : 0}
                  cartQuantity={getProductCartQty ? getProductCartQty(product) : 0}
                  highlightText={highlightText}
                  searchTerm={searchTerm}
                  isSoldOut={isProductSoldOut(product, soldOutItems)}
                  isViewOnly={isViewOnly}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
